import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { openSnackbar } from "../actions/App.Actions";

// ---------------------------------------------------------------------------
// Types – expanded response shape returned by the API
// ---------------------------------------------------------------------------

export interface IDispatchDeskExpandedFlight {
  _id: string;
  flightNumber: number;
  origin: string;
  destination: string;
  scheduledOff?: string;
  scheduledOn?: string;
  actualOff?: string;
  actualOn?: string;
  aircraft?: {
    _id?: string;
    registration?: string;
    serialNumber?: string;
    aircraftType?: {
      _id?: string;
      codeIATA?: string;
      codeICAO?: string;
      title?: string;
    };
  };
  flightType?: any;
}

export interface IDispatchDeskExpandedUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  employeeId?: string;
  preferredName?: string;
}

export interface IDispatchDeskExpanded {
  _id: string;
  name: string;
  color?: string;
  displayOrder?: number;
  flights: IDispatchDeskExpandedFlight[];
  users: IDispatchDeskExpandedUser[];
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface IDispatchDeskState {
  desks: IDispatchDeskExpanded[];
  loading: boolean;
  error: string | null;
}

const initialState: IDispatchDeskState = {
  desks: [],
  loading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/** Fetch all dispatch desks (expanded).
 *  Pass `{ silent: true }` to suppress error notifications (e.g. on app
 *  startup when the user may not have dispatch desk permissions).
 */
export const fetchDispatchDesks = createAsyncThunk(
  "dispatchDesks/fetchAll",
  async (options: { silent?: boolean } | void = {}, thunkApi) => {
    const silent = typeof options === "object" && options?.silent;
    try {
      const response = await AmplifyAPIWrapper.Get<IDispatchDeskExpanded[]>("/dispatchdesks");
      return response ?? [];
    } catch (e: any) {
      if (!silent) {
        thunkApi.dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Failed to load dispatch desks.",
          })
        );
      }
      return thunkApi.rejectWithValue(e?.message ?? "Failed to fetch dispatch desks");
    }
  }
);

/** Add a user to a desk. */
export const addUserToDesk = createAsyncThunk(
  "dispatchDesks/addUser",
  async (payload: { deskId: string; userId: string }, thunkApi) => {
    const { deskId, userId } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Adding user to desk…",
      successMessage: "User added to desk.",
      errorMessage: "Failed to add user to desk.",
    };
    try {
      const response = await AmplifyAPIWrapper.Put<any>(`/dispatchdesk/${deskId}/user`, { userId }, notifications);
      return (response ?? []) as IDispatchDeskExpanded[];
    } catch (e: any) {
      return thunkApi.rejectWithValue(e?.message ?? "Failed to add user");
    }
  }
);

/** Add one or more flights to a desk. */
export const addFlightsToDesk = createAsyncThunk(
  "dispatchDesks/addFlights",
  async (payload: { deskId: string; flightIds: string[] }, thunkApi) => {
    const { deskId, flightIds } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Adding flight to desk…",
      successMessage: "Flight added to desk.",
      errorMessage: "Failed to add flight to desk.",
    };
    try {
      const response = await AmplifyAPIWrapper.Put<any>(
        `/dispatchdesk/${deskId}/flights`,
        { flightIds },
        notifications
      );
      return (response ?? []) as IDispatchDeskExpanded[];
    } catch (e: any) {
      return thunkApi.rejectWithValue(e?.message ?? "Failed to add flight");
    }
  }
);

/** Remove one or more flights from a desk. */
export const removeFlightsFromDesk = createAsyncThunk(
  "dispatchDesks/removeFlights",
  async (payload: { deskId: string; flightIds: string[] }, thunkApi) => {
    const { deskId, flightIds } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Removing flight from desk…",
      successMessage: "Flight removed from desk.",
      errorMessage: "Failed to remove flight from desk.",
    };
    try {
      const response = await AmplifyAPIWrapper.Del<any>(
        `/dispatchdesk/${deskId}/flights`,
        { flightIds },
        notifications
      );
      return (response ?? []) as IDispatchDeskExpanded[];
    } catch (e: any) {
      return thunkApi.rejectWithValue(e?.message ?? "Failed to remove flight");
    }
  }
);

/** Remove a user from a desk. */
export const removeUserFromDesk = createAsyncThunk(
  "dispatchDesks/removeUser",
  async (payload: { deskId: string; userId: string }, thunkApi) => {
    const { deskId, userId } = payload;
    const notifications: INotifications = {
      dispatch: thunkApi.dispatch,
      infoMessage: "Removing user from desk…",
      successMessage: "User removed from desk.",
      errorMessage: "Failed to remove user from desk.",
    };
    try {
      const response = await AmplifyAPIWrapper.Del<any>(`/dispatchdesk/${deskId}/user`, { userId }, notifications);
      return (response ?? []) as IDispatchDeskExpanded[];
    } catch (e: any) {
      return thunkApi.rejectWithValue(e?.message ?? "Failed to remove user");
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const dispatchDeskSlice = createSlice({
  name: "dispatchDesks",
  initialState,
  reducers: {
    /** Allow websocket handler to set desks directly after a re-fetch. */
    setDesks(state, action: PayloadAction<IDispatchDeskExpanded[]>) {
      state.desks = action.payload;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers(builder) {
    // fetchAll
    builder
      .addCase(fetchDispatchDesks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDispatchDesks.fulfilled, (state, action: PayloadAction<IDispatchDeskExpanded[]>) => {
        state.desks = action.payload;
        state.loading = false;
      })
      .addCase(fetchDispatchDesks.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Unknown error";
      });

    // addUser – response is the full desk list
    builder.addCase(addUserToDesk.fulfilled, (state, action: PayloadAction<IDispatchDeskExpanded[]>) => {
      state.desks = action.payload;
    });

    // addFlights – response is the full desk list
    builder.addCase(addFlightsToDesk.fulfilled, (state, action: PayloadAction<IDispatchDeskExpanded[]>) => {
      state.desks = action.payload;
    });

    // removeFlights – response is the full desk list
    builder.addCase(removeFlightsFromDesk.fulfilled, (state, action: PayloadAction<IDispatchDeskExpanded[]>) => {
      state.desks = action.payload;
    });

    // removeUser – response is the full desk list
    builder.addCase(removeUserFromDesk.fulfilled, (state, action: PayloadAction<IDispatchDeskExpanded[]>) => {
      state.desks = action.payload;
    });
  },
});

export const { setDesks } = dispatchDeskSlice.actions;
export default dispatchDeskSlice.reducer;
